import React, { useEffect, useMemo, useState } from 'react'
import { gql, useLazyQuery } from '@apollo/client'
import {
  Button,
  IconAlert,
  IconChecboxSelected,
  IconCheckboxOutline,
  IconIllustratedTopic,
  IconRemove,
  IconRepeat,
  IconSearch,
  ModalLayout
} from '@Knowledge-OTP/znk-ui-components'
import { useDispatch, useSelector } from 'react-redux'
import useTranslation from '../../../../i18n/useTranslation'
import { showAlert } from '../../../../common/Alert/util'
import useChangeEducatorToPlanModal from './useChangeEducatorToPlanModal'
import ChangeEducatorsToPlan from '../../../../Model/Plan/ChangeEducatorsToPlan'
import usePlans from '../../../Student/modal/InfoStudent/useQueries/usePlans'
import './index.css'

const ChangeEducatorToPlan = () => {
  const { isOpen, toggle, plansIds = [], educatorUserIds = [], refetch } = useChangeEducatorToPlanModal()
  const dispatch = useDispatch()
  const services = useSelector((state) => state.user.services)
  // eslint-disable-next-line no-console
  console.log('ChangeEducatorToPlan services:', services)
  const { translate } = useTranslation()
  const [selectedGroupIds, setSelectedGroupIds] = useState([])
  const [pickerGroupId, setPickerGroupId] = useState(null)
  const [educatorSearch, setEducatorSearch] = useState('')
  const [groupActions, setGroupActions] = useState({})
  const [groupEducators, setGroupEducators] = useState({})
  const [getEducatorsByTopic, { data: educatorsData, loading: educatorsLoading }] = useLazyQuery(
    gql`query GetEducatorsByTopic($topicId: ID!, $serviceId: ID!, $teachingTypes: [TeachingType!]) {
      allEducatorByTopic(
        topicId: $topicId
        serviceId: $serviceId
        teachingTypes: $teachingTypes
      ) {
        id
        firstName
        lastName
        avatar
        isActive
        educatorInfo {
          isActive
        }
      }
    }`,
    { fetchPolicy: 'no-cache' }
  )

  useEffect(() => {
    if (isOpen) {
      setSelectedGroupIds([])
      setPickerGroupId(null)
      setEducatorSearch('')
      setGroupActions({})
      setGroupEducators({})
    }
  }, [isOpen])

  const educatorToReplaceId = useMemo(() => {
    return Array.isArray(educatorUserIds) ? educatorUserIds[0] || '' : educatorUserIds || ''
  }, [educatorUserIds])

  const educatorIds = useMemo(() => {
    return (Array.isArray(educatorUserIds) ? educatorUserIds : [educatorUserIds]).filter(Boolean)
  }, [educatorUserIds])

  const [getEducatorPlans, { data: educatorPlansData }] = usePlans()

  const educatorRecommendedEducators = useMemo(() => {
    const plans = educatorPlansData?.plans?.docs || []

    return plans.reduce((recommendedEducators, plan) => {
      const matchingEducators = (plan?.recommendedEducators || []).filter(
        (recommendedEducator) => recommendedEducator?.educatorUserId === educatorToReplaceId
      )

      return recommendedEducators.concat(matchingEducators)
    }, [])
  }, [educatorPlansData, educatorToReplaceId])

  const educatorServicesTopics = useMemo(() => {
    const plans = educatorPlansData?.plans?.docs || []

    const servicesTopics = plans.reduce((result, plan) => {
      const service = services?.find((item) => item?.id === plan?.serviceId)
      if (!service) return result

      const planServicesTopics = (plan?.recommendedEducators || [])
        .filter((recommendedEducator) => recommendedEducator?.educatorUserId === educatorToReplaceId)
        .map((recommendedEducator) => {
          const topic = service?.topics?.find((item) => item?.id === recommendedEducator?.topicId)
          return topic ? `${service.name} - ${topic.name}` : null
        })
        .filter(Boolean)

      return result.concat(planServicesTopics)
    }, [])

    return [...new Set(servicesTopics)]
  }, [educatorPlansData, educatorToReplaceId, services])

  const educatorGroups = useMemo(() => {
    const plans = educatorPlansData?.plans?.docs || []

    return educatorServicesTopics.map((label) => {
      const plansInGroup = plans.filter((plan) => {
        const service = services?.find((item) => item?.id === plan?.serviceId)
        return (plan?.recommendedEducators || [])
          .filter((recommendedEducator) => recommendedEducator?.educatorUserId === educatorToReplaceId)
          .some((recommendedEducator) => {
            const topic = service?.topics?.find((item) => item?.id === recommendedEducator?.topicId)
            return topic && `${service.name} - ${topic.name}` === label
          })
      })

      const blockedRemovePlans = plansInGroup.filter((plan) => {
        const service = services?.find((item) => item?.id === plan?.serviceId)
        const recommendedEducators = plan?.recommendedEducators || []
        const currentEducatorTopics = recommendedEducators
          .filter((recommendedEducator) => recommendedEducator?.educatorUserId === educatorToReplaceId)
          .filter((recommendedEducator) => {
            const topic = service?.topics?.find((item) => item?.id === recommendedEducator?.topicId)
            return topic && `${service.name} - ${topic.name}` === label
          })

        return currentEducatorTopics.some((currentEducatorTopic) => {
          return !recommendedEducators.some((recommendedEducator) => (
            recommendedEducator?.topicId === currentEducatorTopic?.topicId &&
            recommendedEducator?.educatorUserId !== educatorToReplaceId
          ))
        })
      }).length

      return {
        id: label,
        label,
        plans: plansInGroup.length,
        planIds: plansInGroup.map((plan) => plan?._id || plan?.id).filter(Boolean),
        serviceId: plansInGroup[0]?.serviceId || '',
        topicId: plansInGroup
          .flatMap((plan) => (plan?.recommendedEducators || []))
          .find((educator) => educator?.educatorUserId === educatorToReplaceId)?.topicId || '',
        blockedRemovePlans
      }
    })
  }, [educatorPlansData, educatorServicesTopics, educatorToReplaceId, services])

  const currentEducator = educatorRecommendedEducators[0]
  const currentEducatorName = currentEducator
    ? `${currentEducator.firstName || ''} ${currentEducator.lastName || ''}`.trim()
    : 'Current educator'
  const currentEducatorInitials = currentEducatorName
    .split(' ')
    .filter(Boolean)
    .map((namePart) => namePart[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const clearGroupConfiguration = (groupId) => {
    setGroupActions((currentActions) => {
      const nextActions = { ...currentActions }
      delete nextActions[groupId]
      return nextActions
    })
    setGroupEducators((currentEducators) => {
      const nextEducators = { ...currentEducators }
      delete nextEducators[groupId]
      return nextEducators
    })
    setPickerGroupId((currentGroupId) => currentGroupId === groupId ? null : currentGroupId)
    setEducatorSearch('')
  }

  const toggleGroup = (groupId) => {
    if (selectedGroupIds.includes(groupId)) clearGroupConfiguration(groupId)

    setSelectedGroupIds((selectedIds) => selectedIds.includes(groupId)
      ? selectedIds.filter((id) => id !== groupId)
      : [...selectedIds, groupId])
  }

  const toggleAllGroups = () => {
    const shouldClearGroups = selectedGroupIds.length === educatorGroups.length

    if (shouldClearGroups) {
      setGroupActions({})
      setGroupEducators({})
      setPickerGroupId(null)
      setEducatorSearch('')
    }

    setSelectedGroupIds(shouldClearGroups ? [] : educatorGroups.map((group) => group.id))
  }

  const educators = useMemo(() => {
    const search = educatorSearch.trim().toLowerCase()

    return (educatorsData?.allEducatorByTopic || [])
      .filter((educator) => educator?.isActive && educator?.educatorInfo?.isActive !== false)
      .filter((educator) => educator?.id !== educatorToReplaceId)
      .filter((educator) => `${educator.firstName || ''} ${educator.lastName || ''}`.toLowerCase().includes(search))
  }, [educatorSearch, educatorsData, educatorToReplaceId])

  const getEducatorInitials = (educator) => `${educator?.firstName?.[0] || ''}${educator?.lastName?.[0] || ''}`.toUpperCase()

  const openGroupPicker = (groupId) => {
    const group = educatorGroups.find((item) => item.id === groupId)
    setSelectedGroupIds((selectedIds) => selectedIds.includes(groupId) ? selectedIds : [...selectedIds, groupId])
    setGroupActions((currentActions) => ({ ...currentActions, [groupId]: 'replace' }))
    setPickerGroupId((currentGroupId) => currentGroupId === groupId ? null : groupId)
    setEducatorSearch('')

    if (group?.serviceId && group?.topicId) {
      getEducatorsByTopic({
        variables: {
          serviceId: group.serviceId,
          topicId: group.topicId,
          teachingTypes: ['individual', 'group']
        }
      })
    }
  }

  const removeEducatorFromGroup = (group) => {
    if (group.blockedRemovePlans) return

    setSelectedGroupIds((selectedIds) => selectedIds.includes(group.id) ? selectedIds : [...selectedIds, group.id])
    setGroupActions((currentActions) => ({ ...currentActions, [group.id]: 'remove' }))
    setGroupEducators((currentEducators) => {
      const nextEducators = { ...currentEducators }
      delete nextEducators[group.id]
      return nextEducators
    })
    setPickerGroupId((currentGroupId) => currentGroupId === group.id ? null : currentGroupId)
    setEducatorSearch('')
  }

  useEffect(() => {
    if (!pickerGroupId) return undefined

    const closePickerWithEscape = (event) => {
      if (event.key === 'Escape') {
        setPickerGroupId(null)
        setEducatorSearch('')
      }
    }

    document.addEventListener('keydown', closePickerWithEscape)
    return () => document.removeEventListener('keydown', closePickerWithEscape)
  }, [pickerGroupId])

  const selectGroupEducator = (educator) => {
    if (!pickerGroupId) return

    setGroupActions((currentActions) => ({ ...currentActions, [pickerGroupId]: 'replace' }))
    setGroupEducators((currentEducators) => ({ ...currentEducators, [pickerGroupId]: educator }))
    setPickerGroupId(null)
    setEducatorSearch('')
  }

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('Change educator plans:', educatorPlansData)
  }, [educatorPlansData])

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('Recommended educators for current educator:', educatorRecommendedEducators)
  }, [educatorRecommendedEducators])

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('Services and topics for current educator:', educatorServicesTopics)
  }, [educatorServicesTopics])

  useEffect(() => {
    if (isOpen && educatorIds.length) {
      getEducatorPlans({
        variables: {
          limit: 0,
          educatorUserIds: educatorIds,
          status: ['draft', 'sent'],
          kind: ['lessonsPerWeek', 'lessonsFixed', 'counceling']
        }
      })
    }
  }, [isOpen, educatorIds, getEducatorPlans])

  const closeModal = () => {
    toggle()
  }

  const plansCount = plansIds.length
  const selectedGroups = educatorGroups.filter((group) => selectedGroupIds.includes(group.id))
  const readyGroups = selectedGroups.filter((group) => {
    const action = groupActions[group.id]
    if (action === 'replace') return Boolean(groupEducators[group.id])
    return action === 'remove' && !group.blockedRemovePlans
  })
  const affectedPlans = readyGroups.reduce((total, group) => total + group.plans, 0)
  const pendingGroups = selectedGroups.length - readyGroups.length
  const changesReady = readyGroups.length > 0

  const footerNote = !selectedGroups.length
    ? 'Select a group to get started.'
    : pendingGroups
      ? `${affectedPlans ? `${affectedPlans} ${affectedPlans === 1 ? 'plan' : 'plans'} ready · ` : ''}${pendingGroups} ${pendingGroups === 1 ? 'group still needs an action' : 'groups still need an action'}`
      : `${affectedPlans} ${affectedPlans === 1 ? 'plan will be updated' : 'plans will be updated'}`
  const confirmLabel = changesReady
    ? `Update ${affectedPlans} ${affectedPlans === 1 ? 'plan' : 'plans'}`
    : 'Update plans'

  const { submitBatch, loading } = ChangeEducatorsToPlan(
    () => {
      if (typeof refetch === 'function') {
        refetch()
      }
      showAlert({ text: 'Educator changed successfully', status: 'success' }, dispatch)
      closeModal()
    },
    (err) => {
      showAlert({ text: err?.message || 'Unable to change educator', status: 'error' }, dispatch)
    }
  )

  const acceptChange = () => {
    if (!plansIds.length) {
      showAlert({ text: 'No plans were found for the current query', status: 'error' }, dispatch)
      return
    }

    if (!educatorToReplaceId) {
      showAlert({ text: 'Select an educator in the filter first', status: 'error' }, dispatch)
      return
    }

    if (!changesReady) {
      showAlert({ text: 'Complete at least one group before updating plans', status: 'error' }, dispatch)
      return
    }

    if (readyGroups.some((group) => groupActions[group.id] === 'remove')) {
      showAlert({ text: 'Removing an educator is not supported by the current backend operation', status: 'error' }, dispatch)
      return
    }

    const operations = readyGroups.map((group) => ({
      plansIds: group.planIds,
      educatorToReplaceId,
      newEducatorId: groupEducators[group.id].id,
      topicIds: [group.topicId]
    }))

    submitBatch(operations)
  }

  const translatedTitle = translate('plans.actions.changeEducator')
  const title = translatedTitle && translatedTitle !== 'plans.actions.changeEducator'
    ? translatedTitle
    : 'Change educator'

  return (
    <ModalLayout
      className='modal-centered modal-colored change-educator-modal'
      dimensionClassName='change-educator-modal__content'
      isOpen={isOpen}
      toggle={closeModal}
      topHead={
        <div className='change-educator-modal__heading'>
          <span className='change-educator-modal__title'>{title}</span>
          <div className='change-educator-modal__context'>
            <div className='change-educator-modal__avatar'>{currentEducatorInitials || 'ED'}</div>
            <div className='change-educator-modal__context-copy'>
              <span className='change-educator-modal__context-title'>{currentEducatorName}</span>
              <span className='change-educator-modal__context-description'>
                Assigned to {educatorPlansData?.plans?.totalDocs || plansCount} {((educatorPlansData?.plans?.totalDocs || plansCount) === 1) ? 'plan' : 'plans'} across {educatorGroups.length} service and topic groups
              </span>
            </div>
          </div>
        </div>
      }
    >
      <div className='change-educator-modal__body'>
        <div className='change-educator-modal__section-label'>
          <span>Choose what happens to each group</span>
          <span className='change-educator-modal__section-line' />
          <button
            type='button'
            className='change-educator-modal__select-all'
            onClick={toggleAllGroups}
            disabled={!educatorGroups.length}
          >
            {selectedGroupIds.length === educatorGroups.length && educatorGroups.length ? 'Clear selection' : 'Select all groups'}
          </button>
        </div>

        <div className='change-educator-modal__groups'>
          {educatorGroups.map((group) => {
            const isSelected = selectedGroupIds.includes(group.id)
            const action = groupActions[group.id]
            const chosenEducator = groupEducators[group.id]
            const isPickerOpen = pickerGroupId === group.id

            return (
              <div
                className={`change-educator-modal__group ${isSelected ? 'is-selected' : ''}`}
                key={group.id}
              >
                <button
                  type='button'
                  className='change-educator-modal__group-header'
                  onClick={() => toggleGroup(group.id)}
                >
                  <span className={`change-educator-modal__group-checkbox ${isSelected ? 'is-checked' : ''}`}>
                    {isSelected
                      ? <IconChecboxSelected size={18} />
                      : <IconCheckboxOutline size={18} />}
                  </span>
                  <span className='change-educator-modal__group-label'>{group.label}</span>
                  <span className='change-educator-modal__group-plans'>
                    {group.plans} {group.plans === 1 ? 'plan' : 'plans'}
                  </span>
                </button>

                {isSelected && (
                  <div className='change-educator-modal__group-controls'>
                    <div className='change-educator-modal__group-actions'>
                      <button
                        type='button'
                        className={`change-educator-modal__group-action ${action === 'replace' ? 'is-active' : ''}`}
                        onClick={() => openGroupPicker(group.id)}
                      >
                        <IconRepeat size={15} />
                        Replace with a new educator
                      </button>
                      <button
                        type='button'
                        className={`change-educator-modal__group-action change-educator-modal__group-action--remove ${action === 'remove' ? 'is-active' : ''}`}
                        disabled={Boolean(group.blockedRemovePlans)}
                        onClick={() => removeEducatorFromGroup(group)}
                      >
                        <IconRemove size={15} />
                        Remove
                      </button>
                    </div>

                    {Boolean(group.blockedRemovePlans) && (
                      <div className='change-educator-modal__group-warning'>
                        <IconAlert size={15} />
                        <span>
                          Can't remove — {group.blockedRemovePlans} {group.blockedRemovePlans === 1 ? 'plan' : 'plans'} in this group would be left with no educator. Replace instead.
                        </span>
                      </div>
                    )}

                    {action === 'remove' && (
                      <div className='change-educator-modal__remove-note'>
                        {currentEducatorName} will be removed from {group.plans} {group.plans === 1 ? 'plan' : 'plans'}. No replacement will be assigned.
                      </div>
                    )}

                    {isPickerOpen && (
                      <div className='change-educator-modal__picker'>
                        <div className='change-educator-modal__picker-search'>
                          <IconSearch size={16} />
                          <input
                            value={educatorSearch}
                            onChange={(event) => setEducatorSearch(event.target.value)}
                            placeholder='Search educators by name'
                            className='change-educator-modal__picker-search-input'
                          />
                        </div>
                        <div className='change-educator-modal__picker-results'>
                          {educatorsLoading && <div className='change-educator-modal__picker-message'>Loading educators...</div>}
                          {!educatorsLoading && educators.map((educator) => (
                            <button
                              type='button'
                              className='change-educator-modal__picker-option'
                              key={educator.id}
                              onClick={() => selectGroupEducator(educator)}
                            >
                              <span className='change-educator-modal__picker-avatar'>{getEducatorInitials(educator)}</span>
                              <span>{educator.firstName} {educator.lastName}</span>
                            </button>
                          ))}
                          {!educatorsLoading && !educators.length && (
                            <div className='change-educator-modal__picker-empty'>
                              <IconIllustratedTopic size={64} />
                              <span className='change-educator-modal__picker-empty-title'>No educators match that name</span>
                              <span className='change-educator-modal__picker-empty-description'>
                                Check the spelling, or search by first name only. Only educators approved for this service are listed.
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {action === 'replace' && chosenEducator && !isPickerOpen && (
                      <div className='change-educator-modal__chosen-educator'>
                        <span className='change-educator-modal__chosen-avatar'>{getEducatorInitials(chosenEducator)}</span>
                        <span className='change-educator-modal__chosen-copy'>
                          <span className='change-educator-modal__chosen-name'>
                            {chosenEducator.firstName} {chosenEducator.lastName}
                          </span>
                          <span className='change-educator-modal__chosen-description'>
                            Will take over {group.plans} {group.plans === 1 ? 'plan' : 'plans'}
                          </span>
                        </span>
                        <button
                          type='button'
                          className='change-educator-modal__chosen-change'
                          onClick={() => openGroupPicker(group.id)}
                        >
                          Change
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className='change-educator-modal__footer'>
          <span className='change-educator-modal__footer-note'>
            {footerNote}
          </span>
          <Button
            color='secondary'
            label={translate('misc.cancel') || 'Cancel'}
            onClick={closeModal}
          />
          <Button
            color='primary'
            label={confirmLabel}
            activity={loading}
            disabled={!changesReady}
            onClick={acceptChange}
          />
        </div>
      </div>
    </ModalLayout>
  )
}

export default ChangeEducatorToPlan
